import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, UserGroupIcon } from '@heroicons/react/24/solid';

export default function SocialNetworkFeature() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-pri-dark text-white">
      {/* Header with navigation back */}
      <header className="bg-pri-dark/90 backdrop-blur-md fixed top-0 w-full z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img
              className="h-10 w-auto"
              src={`${process.env.REACT_APP_CDN_URL}/logo/logo-no-bg.png`}
              alt="Jingleez"
            />
          </Link>
          
          <Link
            to="/"
            className="inline-flex items-center text-sm font-semibold rounded-full px-4 py-2 bg-white/10 hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
        </div>
      </header>

      <main className="pt-24 pb-16">
        {/* Hero section */}
        <section className="relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm mb-4">
                  <UserGroupIcon className="h-4 w-4 text-mint-green mr-2" />
                  <span className="text-white text-xs font-medium">Feature Spotlight</span>
                </div>
                
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-soft-pink">
                  Social Network
                </h1>
                
                <div className="h-1 w-20 bg-mint-green mb-6"></div>
                
                <p className="text-lg sm:text-xl text-pri-gray/90 mb-8 max-w-xl">
                  Connect with family and friends in a private, secure environment designed to strengthen your real-world relationships.
                </p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="relative rounded-2xl overflow-hidden shadow-2xl"
              >
                <img 
                  src={`${process.env.REACT_APP_CDN_URL}/illustrations/connect.png`}
                  alt="Social Network Interface" 
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-pri-dark to-transparent opacity-30"></div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Key benefits section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.h2 
              className="text-3xl font-bold mb-12 text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Key Benefits
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  title: "Private Connections",
                  description: "Build meaningful connections with the people who matter most in a secure, private environment."
                },
                {
                  title: "Seamless Sharing",
                  description: "Share moments, memories, and plans with your trusted circle without algorithm interference.",
                  comingSoon: false
                },
                {
                  title: "Integrated Experience",
                  description: "Enjoy a fully integrated experience with our calendar and other features for coordinated socializing.",
                  comingSoon: false
                }
              ].map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                  className="bg-gradient-to-br from-pri-dark/80 to-sec-dark/90 p-6 rounded-xl border border-white/10 relative"
                >
                  {benefit.comingSoon && (
                    <div className="absolute -top-3 -right-3 bg-soft-pink text-pri-dark text-xs font-semibold px-3 py-1 rounded-full shadow-md">
                      Coming Soon
                    </div>
                  )}
                  <h3 className="text-xl font-semibold mb-4 text-mint-green">{benefit.title}</h3>
                  <p className="text-pri-gray">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Features section */}
        <section className="py-16 bg-gradient-to-b from-pri-dark to-sec-dark">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.h2 
              className="text-3xl font-bold mb-12 text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Features
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                {
                  title: "Event Coordination",
                  description: "Plan gatherings and events with integrated calendar features for seamless coordination."
                },
                {
                  title: "Private Messaging",
                  description: "End-to-end encrypted messaging for secure conversations with friends and family.",
                  comingSoon: true
                },
                {
                  title: "Photo Sharing",
                  description: "Share photos and memories with select groups without losing ownership of your content.",
                  comingSoon: true
                },
                {
                  title: "Interest Groups",
                  description: "Create and join groups based on shared interests with your connections.",
                  comingSoon: true
                }
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                  className="bg-gradient-to-br from-pri-dark/80 to-sec-dark/90 p-6 rounded-xl border border-white/10 hover:border-white/20 transition-all duration-300 relative"
                >
                  {feature.comingSoon && (
                    <div className="absolute -top-3 -right-3 bg-soft-pink text-pri-dark text-xs font-semibold px-3 py-1 rounded-full shadow-md">
                      Coming Soon
                    </div>
                  )}
                  <h3 className="text-xl font-semibold mb-3 text-soft-pink">{feature.title}</h3>
                  <p className="text-pri-gray mb-4">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-gradient-to-r from-soft-pink/20 to-mint-green/20 rounded-2xl p-8 md:p-12 text-center"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Connect?</h2>
              <p className="text-lg text-pri-gray mb-8 max-w-2xl mx-auto">
                Join our community and start building meaningful digital connections today
              </p>
              <Link
                to="/signup"
                className="inline-flex items-center bg-mint-green text-pri-dark px-8 py-3 rounded-full font-semibold hover:bg-mint-green/90 transition-colors"
              >
                Sign Up Now
              </Link>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-pri-dark py-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="mb-4 md:mb-0">
              <img
                className="h-8 w-auto"
                src={`${process.env.REACT_APP_CDN_URL}/logo/logo-no-bg.png`}
                alt="Jingleez"
              />
            </div>
            <div className="flex space-x-8">
              <Link to="/" className="text-pri-gray hover:text-white transition-colors">
                Home
              </Link>
              <Link to="/about" className="text-pri-gray hover:text-white transition-colors">
                About
              </Link>
              <Link to="/contact" className="text-pri-gray hover:text-white transition-colors">
                Contact
              </Link>
            </div>
          </div>
          <div className="mt-8 pt-4 border-t border-white/10 text-center">
            <p className="text-pri-gray text-sm">© {new Date().getFullYear()} Jingleez. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
} 